import { useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface TourTooltipProps {
  targetId: string;
  title: string;
  message: string;
  step: number;
  totalSteps: number;
  onNext: () => void;
  onSkip: () => void;
  isLastStep?: boolean;
}

export const TourTooltip = ({
  targetId,
  title,
  message,
  step,
  totalSteps,
  onNext,
  onSkip,
  isLastStep = false,
}: TourTooltipProps) => {
  const [position, setPosition] = useState({ top: 0, left: 0, arrowLeft: 0 });
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      const target = document.querySelector(`[data-tour-id="${targetId}"]`);
      if (!target) return;

      const rect = target.getBoundingClientRect();
      setTargetRect(rect);

      const tooltipWidth = 280;
      const padding = 16;
      
      // Position tooltip above the target, centered
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      
      // Keep tooltip within viewport
      if (left < padding) left = padding;
      if (left + tooltipWidth > window.innerWidth - padding) {
        left = window.innerWidth - tooltipWidth - padding;
      }

      // Arrow should point to center of target
      const arrowLeft = rect.left + rect.width / 2 - left;

      setPosition({
        top: rect.top - 16, // 16px gap above target
        left,
        arrowLeft: Math.max(20, Math.min(arrowLeft, tooltipWidth - 20)),
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [targetId]);

  return (
    <>
      {/* Dark overlay with spotlight hole */}
      <div 
        className="fixed inset-0 z-[60] transition-all duration-300"
        style={{
          background: 'rgba(0, 0, 0, 0.75)',
          clipPath: targetRect 
            ? `polygon(
                0% 0%, 
                0% 100%, 
                ${targetRect.left - 8}px 100%, 
                ${targetRect.left - 8}px ${targetRect.top - 8}px, 
                ${targetRect.right + 8}px ${targetRect.top - 8}px, 
                ${targetRect.right + 8}px ${targetRect.bottom + 8}px, 
                ${targetRect.left - 8}px ${targetRect.bottom + 8}px, 
                ${targetRect.left - 8}px 100%, 
                100% 100%, 
                100% 0%
              )`
            : undefined,
        }}
        onClick={onSkip}
      />

      {/* Spotlight glow effect */}
      {targetRect && (
        <div
          className="fixed z-[61] pointer-events-none rounded-xl"
          style={{
            left: targetRect.left - 8,
            top: targetRect.top - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            boxShadow: '0 0 0 4px hsl(var(--primary) / 0.3), 0 0 20px hsl(var(--primary) / 0.2)',
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={cn(
          "fixed z-[62] w-[280px] p-4 rounded-xl",
          "bg-card border-2 border-primary shadow-lg",
          "animate-fade-in"
        )}
        style={{
          bottom: `calc(100vh - ${position.top}px)`,
          left: position.left,
        }}
      >
        {/* Content */}
        <div className="space-y-3">
          <div>
            <h3 className="font-semibold text-foreground text-base">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{message}</p>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "w-2 h-2 rounded-full transition-all duration-300",
                  i === step ? "bg-primary w-4" : "bg-muted"
                )}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-between pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onSkip}
              className="text-muted-foreground hover:text-foreground"
            >
              Passer
            </Button>
            <Button
              size="sm"
              onClick={onNext}
              className="min-w-[100px]"
            >
              {isLastStep ? "C'est parti !" : "Suivant"}
            </Button>
          </div>
        </div>

        {/* Arrow pointing down with pulse animation */}
        <div
          className="absolute -bottom-3 animate-bounce"
          style={{
            left: position.arrowLeft,
            transform: 'translateX(-50%)',
            animation: 'arrow-pulse 1.5s ease-in-out infinite',
          }}
        >
          <div
            className="w-0 h-0"
            style={{
              borderLeft: '12px solid transparent',
              borderRight: '12px solid transparent',
              borderTop: '12px solid hsl(var(--primary))',
            }}
          />
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0"
            style={{
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderTop: '10px solid hsl(var(--card))',
            }}
          />
        </div>
        
        {/* CSS for arrow pulse animation */}
        <style>{`
          @keyframes arrow-pulse {
            0%, 100% {
              transform: translateX(-50%) translateY(0);
              opacity: 1;
            }
            50% {
              transform: translateX(-50%) translateY(4px);
              opacity: 0.7;
            }
          }
        `}</style>
      </div>
    </>
  );
};

